/**
 * AIM — 30 balls, one golden target at a time. Click the golden ball within 2s;
 * wrong click or timeout = lose. Cursor does not collide with balls.
 * Clearing the field: 3s pause (timer full), then 3s fade-in before the next wave.
 */

import { gameRandom } from "./game-rng";

export const AIM_BALLS_TOTAL = 30;
/** Larger than the default 14 ref radius. */
export const AIM_BALL_RADIUS_REF = 22;
export const AIM_BALL_SPEED = 0.85;
export const AIM_ROUND_MS = 2000;
export const AIM_WAVE_PAUSE_MS = 3000;
export const AIM_WAVE_FADE_MS = 3000;

export const AIM_GOLDEN_BALL_HEX = "#ffd84d";
export const AIM_GOLDEN_GLOW_HEX = "#fff8a8";

let roundMs = AIM_ROUND_MS;
let wavePauseMs = AIM_WAVE_PAUSE_MS;

export function setAimTuning(opts?: { roundMs?: number; wavePauseMs?: number }): void {
    roundMs =
        opts?.roundMs != null && Number.isFinite(opts.roundMs) ? Math.max(200, Math.floor(opts.roundMs)) : AIM_ROUND_MS;
    wavePauseMs =
        opts?.wavePauseMs != null && Number.isFinite(opts.wavePauseMs)
            ? Math.max(0, Math.floor(opts.wavePauseMs))
            : AIM_WAVE_PAUSE_MS;
}

function aimRoundMs(): number {
    return roundMs;
}

type AimWavePhase = "none" | "pause" | "fadeIn";

let roundEndsAt = 0;
let nextDisplay = 1;
let pulseUntil = 0;
let frozenRemainingMs = aimRoundMs();
let wavePhase: AimWavePhase = "none";
let wavePauseEndsAt = 0;
let waveFadeStartedAt = 0;

function clamp01(v: number) {
    return Math.max(0, Math.min(1, v));
}

function syncDisplayFromRemaining(remainingMs: number, now: number) {
    const display = Math.ceil(Math.max(0, remainingMs) / 1000);
    if (display !== nextDisplay) {
        nextDisplay = display;
        pulseUntil = now + 180;
    }
}

export function resetAimWaveState() {
    wavePhase = "none";
    wavePauseEndsAt = 0;
    waveFadeStartedAt = 0;
}

export function resetAimState(now: number) {
    resetAimWaveState();
    roundEndsAt = now + aimRoundMs();
    frozenRemainingMs = aimRoundMs();
    nextDisplay = Math.ceil(aimRoundMs() / 1000);
    pulseUntil = now + 180;
}

export function resetAimTimer(now: number) {
    roundEndsAt = now + aimRoundMs();
    frozenRemainingMs = aimRoundMs();
    nextDisplay = Math.ceil(aimRoundMs() / 1000);
    pulseUntil = now + 180;
}

export function scheduleAimWaveClearPause(now: number) {
    if (wavePhase !== "none") return;
    wavePhase = "pause";
    wavePauseEndsAt = now + wavePauseMs;
    frozenRemainingMs = aimRoundMs();
    nextDisplay = Math.ceil(aimRoundMs() / 1000);
}

export function isAimWaveActive() {
    return wavePhase !== "none";
}

export function isAimInputBlocked(now: number) {
    return wavePhase === "pause" || wavePhase === "fadeIn";
}

export function isAimTimerFrozen(now: number, paused: boolean) {
    return paused || isAimInputBlocked(now);
}

/** Returns when fade-in should spawn balls, or when the wave is fully ready. */
export function tickAimWave(now: number): "startFadeIn" | "fadeComplete" | null {
    if (wavePhase === "pause" && now >= wavePauseEndsAt) {
        wavePhase = "fadeIn";
        waveFadeStartedAt = now;
        return "startFadeIn";
    }
    if (wavePhase === "fadeIn" && now >= waveFadeStartedAt + AIM_WAVE_FADE_MS) {
        wavePhase = "none";
        return "fadeComplete";
    }
    return null;
}

export function getAimWaveFadeAlpha(now: number) {
    if (wavePhase !== "fadeIn") return 1;
    return clamp01((now - waveFadeStartedAt) / AIM_WAVE_FADE_MS);
}

export function tickAimTimer(now: number, frozen: boolean): boolean {
    if (roundEndsAt <= 0) return false;
    if (frozen) {
        syncDisplayFromRemaining(frozenRemainingMs, now);
        return false;
    }
    const remaining = roundEndsAt - now;
    frozenRemainingMs = Math.max(0, remaining);
    syncDisplayFromRemaining(remaining, now);
    return remaining <= 0;
}

export function getAimHudRemainingMs(now: number, paused: boolean) {
    if (paused || isAimWaveActive()) return aimRoundMs();
    return Math.max(0, roundEndsAt - now);
}

export function isAimTimerPulsing(now: number) {
    return now < pulseUntil;
}

export function getAimTimeFraction(now: number, paused = false) {
    return clamp01(getAimHudRemainingMs(now, paused) / aimRoundMs());
}

export function shiftAimPauseTimers(deltaMs: number) {
    if (deltaMs <= 0) return;
    roundEndsAt = roundEndsAt > 0 ? roundEndsAt + deltaMs : roundEndsAt;
    pulseUntil = pulseUntil > 0 ? pulseUntil + deltaMs : pulseUntil;
    wavePauseEndsAt = wavePauseEndsAt > 0 ? wavePauseEndsAt + deltaMs : wavePauseEndsAt;
    waveFadeStartedAt = waveFadeStartedAt > 0 ? waveFadeStartedAt + deltaMs : waveFadeStartedAt;
}

export function getAimBallDrawAlpha(isGolden: boolean) {
    return isGolden ? 1 : 0.55;
}

export function getAimBallNeonStrength(isGolden: boolean) {
    return isGolden ? 1 : 0.28;
}

/** Pick one random obstacle as the golden target. */
export function assignRandomAimGolden<T extends { isAimGolden?: boolean }>(obstacles: T[]) {
    for (const o of obstacles) o.isAimGolden = false;
    if (obstacles.length === 0) return;
    const idx = Math.floor(gameRandom() * obstacles.length);
    obstacles[idx]!.isAimGolden = true;
}

/** Clear golden on `previousGolden` and pick a different random target (ball stays). */
export function reassignAimGoldenFrom<T extends { isAimGolden?: boolean }>(
    obstacles: T[],
    previousGolden: T,
) {
    for (const o of obstacles) o.isAimGolden = false;
    const pool = obstacles.filter(o => o !== previousGolden);
    if (pool.length === 0) {
        previousGolden.isAimGolden = true;
        return;
    }
    pool[Math.floor(gameRandom() * pool.length)]!.isAimGolden = true;
}

/** Bottom time bar + countdown (2s rounds). */
export function drawAimTimeHud(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    layoutScale: number,
    now: number,
    paused = false,
) {
    if (w < 40 || h < 40) return;

    const remainingMs = getAimHudRemainingMs(now, paused);
    const displaySec = Math.ceil(remainingMs / 1000);
    const margin = Math.max(12, 14 * layoutScale);
    const barH = Math.max(5, 6 * layoutScale);
    const barW = Math.min(w * 0.34, w - margin * 2);
    const barX = (w - barW) * 0.5;
    const barY = h - margin - barH;
    const frac = clamp01(remainingMs / aimRoundMs());
    const r = Math.min(barH * 0.45, 4 * layoutScale);

    ctx.save();

    ctx.fillStyle = "rgba(0,0,0,0.45)";
    ctx.beginPath();
    ctx.roundRect(barX, barY, barW, barH, r);
    ctx.fill();

    if (frac > 0) {
        const fillW = Math.max(barH, barW * frac);
        const grad = ctx.createLinearGradient(barX, 0, barX + barW, 0);
        grad.addColorStop(0, "#ffd84d");
        grad.addColorStop(0.55, "#fff5c8");
        grad.addColorStop(1, frac < 0.25 ? "#ff5555" : "#ffe88a");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.roundRect(barX, barY, fillW, barH, r);
        ctx.fill();
    }

    ctx.strokeStyle = "rgba(255,255,255,0.35)";
    ctx.lineWidth = Math.max(1, 1.2 * layoutScale);
    ctx.beginPath();
    ctx.roundRect(barX, barY, barW, barH, r);
    ctx.stroke();

    const pulse = isAimTimerPulsing(now);
    const timerSize = (pulse ? 20 : 17) * layoutScale;
    const timerY = barY - Math.max(9, 10 * layoutScale);
    ctx.font = `${timerSize}px 'Press Start 2P', monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillStyle = "#ffffff";
    ctx.shadowColor = "rgba(255,248,168,0.55)";
    ctx.shadowBlur = pulse ? 14 : 8;
    ctx.fillText(String(displaySec), w * 0.5, timerY);
    ctx.shadowBlur = 0;

    ctx.restore();
}
